import { AutoPostSlot } from 'services/post/state'
import { PlannedPost } from 'shared/types'
import { sortAscending } from 'shared/utils'

type ReorderResult = {
  posts: PlannedPost[]
  ids: string[]
}

export function getReorderedPosts(
  movedPost: PlannedPost,
  targetPost: PlannedPost,
  posts: PlannedPost[],
  autoPostSlots: AutoPostSlot[]
): ReorderResult | null {
  const { ppPageId: ppid, bucketId } = movedPost

  const autoPostsByProfile = posts
    .filter(p => p.autoPost && !p.isEmpty && !p.isVirtual && !p.isPosted && p.ppPageId === ppid && p.bucketId === bucketId)
    .sort(sortAscending('autoPostOrder')) as PlannedPost[]

  let currentIndex = -1
  let nextIndex = -1
  for (let i = 0; i < autoPostsByProfile.length; i++) {
    const pid = autoPostsByProfile[i].id
    if (pid === movedPost.id) currentIndex = i
    if (pid === targetPost.id) nextIndex = i
    if (currentIndex !== -1 && nextIndex !== -1) break
  }

  if (currentIndex === -1 || nextIndex === -1 || currentIndex === nextIndex) {
    return null
  }

  autoPostsByProfile.splice(currentIndex, 1)
  autoPostsByProfile.splice(nextIndex, 0, movedPost)

  const slots = autoPostSlots.filter(s => s.ppPageId === ppid && s.bucketId === bucketId)
  const updatedMap = new Map<string, PlannedPost>()
  const ids: string[] = []

  for (let i = 0; i < autoPostsByProfile.length; i++) {
    const post = autoPostsByProfile[i]
    const slot = slots[i]
    ids.push(post.id)
    if (slot) {
      updatedMap.set(post.id, {
        ...post,
        time: slot.time,
        dateString: slot.dateString,
        weekday: slot.weekday,
        autoPostOrder: i,
        timeString: slot.timeString,
        hoursLocal: slot.hoursLocal
      })
    }
  }

  const updatedPosts = posts.map(post => updatedMap.get(post.id) || post)
  updatedPosts.sort(sortAscending('time'))

  return { posts: updatedPosts, ids }
}

type MoveResult = {
  posts: PlannedPost[]
  ids: string[]
  moveTo?: 'top' | 'bottom'
}

export function getMovedPostResult(
  movedPost: PlannedPost,
  position: 'top' | 'bottom' | 'up' | 'down',
  posts: PlannedPost[],
  autoPostSlots: AutoPostSlot[]
): MoveResult | null {
  const { id, ppPageId: ppid, bucketId } = movedPost

  const autoPostsByProfile = posts
    .filter(p => p.autoPost && !p.isEmpty && !p.isVirtual && !p.isPosted && p.bucketId === bucketId && p.ppPageId === ppid)
    .sort(sortAscending('autoPostOrder')) as PlannedPost[]

  const ids = autoPostsByProfile.map(p => p.id)
  const currentIndex = ids.indexOf(id)
  if (currentIndex === -1) return null

  ids.splice(currentIndex, 1)

  switch (position) {
    case 'top':
      ids.unshift(id)
      break
    case 'bottom':
      ids.push(id)
      break
    case 'up':
      if (currentIndex > 0) {
        ids.splice(currentIndex - 1, 0, id)
      } else {
        return null
      }
      break
    case 'down':
      if (currentIndex < ids.length) {
        ids.splice(currentIndex + 1, 0, id)
      } else {
        return null
      }
      break
    default:
      return null
  }

  const postMap = new Map(autoPostsByProfile.map(p => [p.id, p]))
  const reorderedPosts = ids.map(pid => postMap.get(pid)!)

  const slots = autoPostSlots.filter(s => s.ppPageId === ppid && s.bucketId === bucketId)
  const updatedMap = new Map<string, PlannedPost>()

  for (let i = 0; i < reorderedPosts.length; i++) {
    const post = reorderedPosts[i]
    const slot = slots[i]
    if (slot) {
      updatedMap.set(post.id, {
        ...post,
        time: slot.time,
        dateString: slot.dateString,
        weekday: slot.weekday,
        autoPostOrder: i,
        timeString: slot.timeString,
        hoursLocal: slot.hoursLocal
      })
    }
  }

  const updatedPosts = posts.map(post => updatedMap.get(post.id) || post)
  updatedPosts.sort(sortAscending('time'))

  const moveTo = position === 'top' || position === 'bottom' ? position : undefined

  return { posts: updatedPosts, ids, moveTo }
}
